import type { JSONContent } from '@/features/editor/unified';

/**
 * Error thrown when offset mapping fails
 */
export class OffsetMappingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OffsetMappingError';
  }
}

/**
 * Path to a specific position in a TipTap document
 */
interface TipTapPath {
  indices: number[];  // Path from root to node (e.g., [0, 2] = first paragraph, third child)
  nodeType: string;   // "text" | "paragraph" | "hardBreak" | etc.
  charOffset: number; // Character offset within text node (0 for non-text nodes)
}

/**
 * A span of text identified by start/end paths
 */
export interface TipTapSpan {
  start_path: number[];
  start_char: number;
  end_path: number[];
  end_char: number;
}

/**
 * Map a plain text offset to its corresponding TipTap JSON path.
 * This implements the same algorithm as the backend's map_offset_to_path function.
 *
 * @param bodyJson - TipTap document structure (must have "type": "doc")
 * @param plainTextOffset - Character offset in the plain text representation
 * @returns TipTapPath with indices, node type, and char offset
 * @throws OffsetMappingError if offset is invalid or document structure is malformed
 */
function mapOffsetToPath(
  bodyJson: JSONContent | Record<string, unknown>,
  plainTextOffset: number
): TipTapPath {
  // Validation: Offset must be non-negative
  if (plainTextOffset < 0) {
    throw new OffsetMappingError(`Offset must be non-negative, got: ${plainTextOffset}`);
  }

  // Cast to JSONContent - the validation below will ensure it's valid
  const doc = bodyJson as JSONContent;

  // Validation: Document structure
  if (doc.type !== 'doc') {
    throw new OffsetMappingError(
      `Invalid document structure: expected type='doc', got type='${String(doc.type)}'`
    );
  }

  if (!doc.content || !Array.isArray(doc.content)) {
    throw new OffsetMappingError("Invalid document structure: missing or invalid 'content' array");
  }

  // Validate non-empty document
  const content = doc.content;
  if (content.length === 0) {
    if (plainTextOffset === 0) {
      throw new OffsetMappingError('Cannot map offset in empty document (no content nodes)');
    }
    throw new OffsetMappingError(
      `Offset ${plainTextOffset} exceeds document length (document is empty)`
    );
  }

  // Build offset map by traversing document
  let currentOffset = 0;
  let resultPath: TipTapPath | null = null;
  let fallbackPath: TipTapPath | null = null;
  let lastTextNode: { path: number[]; textLen: number } | null = null;

  function traverse(node: JSONContent, path: number[]): number {
    // If we already found the target, stop
    if (resultPath !== null) {
      return currentOffset;
    }

    const nodeType = node.type || '';

    // Text node: contains actual text content
    if (nodeType === 'text') {
      const text = node.text || '';
      const textLen = text.length;

      // Track this as the last text node seen
      lastTextNode = { path, textLen };

      // Check if target offset falls within this text node (excluding upper boundary)
      // Boundary offsets are handled by fallback logic below
      if (currentOffset <= plainTextOffset && plainTextOffset < currentOffset + textLen) {
        const charOffset = plainTextOffset - currentOffset;
        resultPath = {
          indices: path,
          nodeType: 'text',
          charOffset,
        };
      }

      currentOffset += textLen;

      // Boundary case: offset at end of this text node
      // Set as fallback - will be used only if no subsequent node claims this offset
      // This handles cases where offset is at end of document or before a newline
      if (plainTextOffset === currentOffset && resultPath === null) {
        fallbackPath = {
          indices: path,
          nodeType: 'text',
          charOffset: textLen,
        };
      }

      return currentOffset;
    }

    // Hard break node: doesn't contribute to plain text offset
    if (nodeType === 'hardBreak') {
      return currentOffset;
    }

    // Container node: recursively process children
    const children = node.content;
    if (Array.isArray(children)) {
      for (let idx = 0; idx < children.length; idx++) {
        const child = children[idx];
        if (child) {
          const childPath = [...path, idx];
          currentOffset = traverse(child, childPath);
          if (resultPath !== null) {
            return currentOffset;
          }
        }
      }
    }

    // Block nodes add newline after their content
    if (nodeType === 'paragraph' || nodeType === 'heading' || nodeType === 'blockquote' || nodeType === 'code_block') {
      currentOffset += 1;
    }

    return currentOffset;
  }

  // Start traversal from root content
  for (let idx = 0; idx < content.length; idx++) {
    const node = content[idx];
    if (node) {
      traverse(node, [idx]);
      if (resultPath !== null) {
        return resultPath;
      }
    }
  }

  // If no definitive match, use fallback (boundary offset before newline)
  if (resultPath === null && fallbackPath !== null) {
    return fallbackPath;
  }

  // Special case: offset is at end of document
  // TypeScript: lastTextNode is guaranteed non-null here if offset === currentOffset
  // because we must have traversed at least one text node to reach currentOffset
  if (resultPath === null && plainTextOffset === currentOffset && lastTextNode !== null) {
    const { path, textLen } = lastTextNode;
    return {
      indices: path,
      nodeType: 'text',
      charOffset: textLen,
    };
  }

  // If we reached here, offset was beyond document length
  throw new OffsetMappingError(
    `Offset ${plainTextOffset} exceeds document length (final offset: ${currentOffset})`
  );
}

/**
 * Map a plain text offset range to TipTap span.
 * This implements the same algorithm as the backend's map_offset_range_to_span function.
 *
 * @param bodyJson - TipTap document structure
 * @param startOffset - Start position in plain text (inclusive)
 * @param endOffset - End position in plain text (exclusive)
 * @returns TipTapSpan with separate start and end paths
 * @throws OffsetMappingError if offsets are invalid or document structure is malformed
 */
export function mapOffsetRangeToSpan(
  bodyJson: JSONContent | Record<string, unknown>,
  startOffset: number,
  endOffset: number
): TipTapSpan {
  // Validation: Range must be valid
  if (endOffset <= startOffset) {
    throw new OffsetMappingError(
      `End offset must be greater than start offset: start=${startOffset}, end=${endOffset}`
    );
  }

  // Map both offsets to paths
  const startPath = mapOffsetToPath(bodyJson, startOffset);
  const endPath = mapOffsetToPath(bodyJson, endOffset);

  return {
    start_path: startPath.indices,
    start_char: startPath.charOffset,
    end_path: endPath.indices,
    end_char: endPath.charOffset,
  };
}
